/**
 * Result-rendering sub-component for GlobalSearch.
 *
 * Extracted into its own file ONLY so the visual-edits Babel plugin doesn't
 * blow its stack on the deeply nested CommandGroup / CommandItem JSX of the
 * parent. Keep this file flat — no further nested ternaries inside the
 * returned JSX.
 */
import React from 'react';
import { CommandGroup, CommandItem } from '@/components/ui/command';
import { Users, FileText, Receipt } from 'lucide-react';

const TravellerRow = ({ c, go }) => (
  <CommandItem
    value={`traveller-${c.ucc}-${c.displayUCC || ''}-${c.fullName || c.name || ''}`}
    onSelect={() => go(`/clients/${c.ucc}`)}
    data-testid={`gs-traveller-${c.ucc}`}
  >
    <Users className="h-4 w-4 mr-2 text-slate-500" />
    <span className="flex-1 truncate">{c.fullName || c.name || c.email || '—'}</span>
    <span className="text-xs text-slate-400 font-mono">{c.displayUCC || c.ucc}</span>
  </CommandItem>
);

const TripRow = ({ t, go }) => {
  const pax = (t.adults || 0) + (t.children || 0) + (t.infants || 0);
  return (
    <CommandItem
      value={`trip-${t.tripId}-${t.displayTripId || ''}-${t.destination || ''}`}
      onSelect={() => go(`/quotes/${t.id || t.tripId}/edit`)}
      data-testid={`gs-trip-${t.tripId}`}
    >
      <FileText className="h-4 w-4 mr-2 text-slate-500" />
      <span className="flex-1 truncate">{t.destination || 'Trip'} · {pax} pax</span>
      <span className="text-xs text-slate-400 font-mono">{t.displayTripId || t.tripId}</span>
    </CommandItem>
  );
};

const InvoiceRow = ({ inv, go }) => {
  const clientName = (inv.client && inv.client.name) || '';
  return (
    <CommandItem
      value={`invoice-${inv.invoiceNumber}`}
      onSelect={() => go('/invoices')}
      data-testid={`gs-invoice-${inv.invoiceNumber}`}
    >
      <Receipt className="h-4 w-4 mr-2 text-slate-500" />
      <span className="flex-1 truncate">{inv.invoiceNumber}</span>
      <span className="text-xs text-slate-400 font-mono">{clientName}</span>
    </CommandItem>
  );
};

const SearchResults = ({ q, loading, results, go }) => {
  if (q.length < 2) {
    return <div className="py-6 text-center text-xs text-slate-400">Type at least 2 characters to search.</div>;
  }
  if (loading) {
    return <div className="py-6 text-center text-xs text-slate-400">Searching…</div>;
  }

  const travellers = results.travellers || [];
  const trips = results.trips || [];
  const invoices = results.invoices || [];
  const total = travellers.length + trips.length + invoices.length;
  if (total === 0) return null; // let CommandEmpty render

  return (
    <>
      {travellers.length > 0 ? (
        <CommandGroup heading="Travellers">
          {travellers.map((c) => <TravellerRow key={c.ucc || c.id} c={c} go={go} />)}
        </CommandGroup>
      ) : null}
      {trips.length > 0 ? (
        <CommandGroup heading="Trips">
          {trips.map((t) => <TripRow key={t.tripId || t.id} t={t} go={go} />)}
        </CommandGroup>
      ) : null}
      {invoices.length > 0 ? (
        <CommandGroup heading="Invoices">
          {invoices.map((inv) => <InvoiceRow key={inv.invoiceNumber || inv.id} inv={inv} go={go} />)}
        </CommandGroup>
      ) : null}
    </>
  );
};

export default SearchResults;
